import { AxiosResponse } from 'axios'

import googleMeetIcon from 'images/googleMeet.png'
import microsoftTeamsIcon from 'images/microsoftTeams.png'
import zoomIcon from 'images/zoom.png'

import { ICalendarStatus } from '../modules/calendar/interfaces'

import { CustomerAPI, MeetingsAPI } from '.'

export enum ConferenceProvider {
  Zoom = 'ZOOM',
  MicrosoftTeams = 'MICROSOFT_TEAMS',
  GoogleMeet = 'GOOGLE_MEET',
  None = 'NONE',
}

type AuthField = 'zoomAuthed' | 'microsoftAuthed' | 'googleAuthed'

interface ConferenceProviderAuthFields {
  [ConferenceProvider.Zoom]: AuthField
  [ConferenceProvider.MicrosoftTeams]: AuthField
  [ConferenceProvider.GoogleMeet]: AuthField
  [ConferenceProvider.None]: any
}

const ConferenceProviderIntegrationPath = {
  [ConferenceProvider.Zoom]: 'zoom_meetings',
  [ConferenceProvider.MicrosoftTeams]: 'microsoftTeams',
  [ConferenceProvider.GoogleMeet]: 'googleMeet',
  [ConferenceProvider.None]: undefined, // Unused, just makes typescript happy
}

export const ConferenceProviderName = {
  [ConferenceProvider.Zoom]: 'Zoom',
  [ConferenceProvider.MicrosoftTeams]: 'Microsoft Teams',
  [ConferenceProvider.GoogleMeet]: 'Google Meet',
  [ConferenceProvider.None]: undefined, // Unused, just makes typescript happy
}

export const ConferenceProviderAuthField: ConferenceProviderAuthFields = {
  [ConferenceProvider.Zoom]: 'zoomAuthed',
  [ConferenceProvider.MicrosoftTeams]: 'microsoftAuthed',
  [ConferenceProvider.GoogleMeet]: 'googleAuthed',
  [ConferenceProvider.None]: undefined, // Unused, just makes typescript happy.
}

export const ConferenceProviderIcons = {
  [ConferenceProvider.Zoom]: zoomIcon,
  [ConferenceProvider.MicrosoftTeams]: microsoftTeamsIcon,
  [ConferenceProvider.GoogleMeet]: googleMeetIcon,
  [ConferenceProvider.None]: undefined, // Unused, just makes typescript happy.
}

interface ProviderState {
  enabled: boolean
  connected: boolean
}

export type ProviderStates = Record<ConferenceProvider, ProviderState>

export const getZoomUser = () => {
  return MeetingsAPI.get(`/${ConferenceProvider.Zoom}/user`)
}

export const getIsConferenceProviderEnabled = (
  provider: ConferenceProvider
): Promise<boolean> =>
  CustomerAPI.get(
    `/integrations/${ConferenceProviderIntegrationPath[provider]}/toggles`
  ).then((res: AxiosResponse<{ enabled: boolean }>) => res.data.enabled)

export const disconnectConferenceProvider = (provider: ConferenceProvider) =>
  MeetingsAPI.post(`/${ConferenceProviderIntegrationPath[provider]}/disconnect`)

export const getConferenceProviderState = async (
  provider: ConferenceProvider,
  authStatus: ICalendarStatus
): Promise<ProviderState> => {
  try {
    const enabled = await getIsConferenceProviderEnabled(provider)
    const authedField: AuthField = ConferenceProviderAuthField[provider]
    const connected: boolean =
      enabled && !!authedField && authStatus[authedField]
    return { enabled, connected }
  } catch (e) {
    return { enabled: false, connected: false }
  }
}

export const getCalendarSettingsStatus = async () =>
  MeetingsAPI.get('/calendar/properties/status/v3').then(
    (res: AxiosResponse<ICalendarStatus>) => res.data
  )

export const getConferenceProviderStates = async () => {
  const providers = Object.values(ConferenceProvider).filter(
    (p) => p !== ConferenceProvider.None
  )
  const settings = await getCalendarSettingsStatus()

  // Retrieve all provider states concurrently
  const statuses = await Promise.all(
    providers.map((p) => getConferenceProviderState(p, settings))
  )

  return providers.reduce(
    (acc, provider, index) => ({
      ...acc,
      [provider]: statuses[index],
    }),
    {} as ProviderStates
  )
}
